<script setup>
const { width } = useWindowSize();

const props = defineProps({
    images: {
        type: Array,
        required: true,
    },
    className: {
        type: String,
        default: "",
    },
});

const isOpen = ref(false);
const currentImage = ref({});
const currentIndex = ref(0);
const galleryRef = ref(null);
const shouldCenter = ref(false);
const isMdScreen = ref(false);

const lightboxWrapper = ref(null);
const imageRefs = ref([]);

// 觸摸事件
const touchStartX = ref(0);
const touchEndX = ref(0);

// 圖片列表靠左，若列表總寬度大於螢幕寬度，就將圖片列表置中
const checkShouldCenter = () => {
    if (!galleryRef.value) return;
    const galleryWidth = galleryRef.value.scrollWidth;
    shouldCenter.value = galleryWidth <= width.value;
};

watch(width, () => {
    isMdScreen.value = width.value >= 1024;
    checkShouldCenter();
});
// 計算要顯示的圖片
const displayedImages = computed(() => {
    return isMdScreen.value ? props.images.slice(0, 3) : props.images;
});

const open = (image, index) => {
    currentImage.value = image;
    currentIndex.value = index;
    isOpen.value = true;
    document.body.style.overflow = "hidden";

    nextTick(() => {
        checkShouldCenter();
    });
};

const close = () => {
    isOpen.value = false;
    document.body.style.overflow = "";
};

const changeImage = (image, index) => {
    currentImage.value = image;
    currentIndex.value = index;
};

const prevImage = () => {
    currentIndex.value =
        (currentIndex.value - 1 + props.images.length) % props.images.length;
    currentImage.value = props.images[currentIndex.value];
};

const nextImage = () => {
    currentIndex.value = (currentIndex.value + 1) % props.images.length;
    currentImage.value = props.images[currentIndex.value];
};

// swipe main picture
const handleTouchStart = (e) => {
    touchStartX.value = e.touches[0].clientX;
};
const handleTouchMove = (e) => {
    touchEndX.value = e.touches[0].clientX;
};
const handleTouchEnd = () => {
    const diff = touchStartX.value - touchEndX.value;
    const threshold = 50; // 滑動閾值

    if (Math.abs(diff) > threshold) {
        diff > 0 ? nextImage() : prevImage();
    }
};

useClickOutsideClose(
    [
        ".lightbox-main",
        ".lightbox-thumbnail",
        "._button-close",
        "._button-next",
        "._button-pre",
    ],
    {
        onClose: (e) => {
            if (isOpen.value && e.target.classList.contains("slt-container"))
                close();
        },
        lockScroll: false,
    },
);
const { isPressed, pressClose } = usePressClose(close);

onMounted(() => {
    nextTick(() => {
        // md 以上只放三張圖，所以不用設定要第二張圖置中
        if (width.value >= 1024) return;

        // 如果有三張圖片以上，才設定第二張圖置中
        if (imageRefs.value[2]) {
            const imageWidth = imageRefs.value[0].offsetWidth;
            const gap = 12;
            const padding = 16;

            // 計算第二張圖位置
            const scrollPosition =
                imageWidth + padding + gap - width.value / 2 + imageWidth / 2;

            lightboxWrapper.value?.scrollTo({
                left: scrollPosition,
                behavior: "smooth",
            });
        }
    });
});
</script>

<template>
    <ClientOnly>
        <Transition name="fade">
            <!-- 圖片燈箱 -->
            <section
                v-if="isOpen"
                class="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black bg-opacity-90"
            >
                <div
                    class="slt-container relative flex h-full w-full flex-col items-center justify-center"
                >
                    <button
                        @click="pressClose"
                        :class="{ 'animate-buttonPress': isPressed }"
                        class="_button-close flex items-center justify-center transition-transform hover:scale-105 focus:scale-95"
                    >
                        <SvgClose24 class="h-[6.1vw] w-[6.1vw] xs:h-6 xs:w-6" />
                    </button>

                    <button
                        @click="prevImage"
                        class="_button-next adjust-height"
                    >
                        <SvgChevronLeft class="h-10 w-10" />
                    </button>

                    <button
                        @click="nextImage"
                        class="_button-pre adjust-height"
                    >
                        <SvgChevronRight class="h-10 w-10" />
                    </button>

                    <!-- Lightbox 主圖片 -->
                    <div
                        class="adjust-height relative mx-auto h-[61.06vw] w-[91.34vw] overflow-hidden rounded-lg sm:h-[59.89vw] sm:max-h-[460px] sm:w-[89.32vw] sm:max-w-[686px] md:h-[34.06vw] md:max-h-[515px] md:w-[50.79vw] md:max-w-[768px]"
                        @touchstart.passive="handleTouchStart"
                        @touchmove.passive="handleTouchMove"
                        @touchend="handleTouchEnd"
                    >
                        <img
                            :src="currentImage.source_url"
                            :alt="currentImage.alt_text"
                            class="lightbox-main h-full w-full object-cover"
                        />
                    </div>
                </div>
                <!-- 預覽圖 -->
                <figure
                    class="lightbox-thumbnail slt-scrollbar-hide absolute bottom-10 flex w-full items-end gap-2 overflow-x-auto px-4"
                    :class="{
                        'justify-center': shouldCenter,
                    }"
                    ref="galleryRef"
                >
                    <div
                        v-for="(image, index) in images"
                        :key="index"
                        :class="[
                            {
                                'transition-all duration-300 lg:h-[14.67vw] lg:max-h-[121px] lg:min-h-[82.75px]':
                                    index === currentIndex,
                            },
                        ]"
                        class="group relative h-[20.03vw] max-h-[100px] min-h-[78.75px] w-[27.55vw] min-w-[108.28px] max-w-[154px] flex-shrink-0 cursor-pointer sm:h-[13.67vw] sm:max-h-[112px] sm:w-[18.79vw] md:w-[18.19vw] lg:w-[15.42vw]"
                    >
                        <div
                            class="pointer-events-none absolute inset-0 z-10 size-full bg-base-black-80 transition-opacity duration-300 group-hover:opacity-0"
                            :class="{
                                'opacity-0': index === currentIndex,
                                'opacity-70': index !== currentIndex,
                            }"
                        ></div>
                        <img
                            :src="image.source_url"
                            :alt="image.alt_text"
                            @click="changeImage(image, index)"
                            class="h-full w-full rounded-lg object-cover"
                        />
                    </div>
                </figure>
            </section>
        </Transition>
    </ClientOnly>
    <!-- 圖片區：點擊展開圖片燈箱 -->
    <figure
        :class="props.className"
        ref="lightboxWrapper"
        class="slt-scrollbar-hide mx-auto flex gap-3 overflow-x-auto px-4 md:w-[89.71vw] md:max-w-[var(--slt-container-width-sm)] md:justify-center md:p-0 lg:w-[71vw]"
    >
        <div
            v-for="(image, index) in displayedImages"
            :key="index"
            class="flex-shrink-0 cursor-pointer md:flex-1"
        >
            <img
                :src="image.source_url"
                :alt="image.alt_text"
                :ref="(el) => (imageRefs[index] = el)"
                loading="lazy"
                @click="open(image, index)"
                class="h-[224px] w-[330.67px] rounded-2xl object-cover md:h-[20vw] md:w-full lg:h-[16vw] 3xl:h-[305px]"
            />
        </div>
    </figure>
</template>

<style scoped>
.adjust-height {
    @apply -mt-24;
}

._button-close {
    @apply absolute right-[4.07vw] top-[4.07vw] z-[5] h-[10.18vw] rounded-full bg-primary-orange px-[4.07vw] py-[2.03vw] xs:right-[16px] xs:top-[16px] xs:h-[40px] xs:px-[16px] xs:py-[8px] sm:right-[32px] sm:top-[32px] sm:h-[48px] sm:px-[20px] sm:py-[16px];
}
._button-next {
    @apply absolute left-[40px] top-1/2 hidden -translate-x-5 transform p-5 lg:block;
}
._button-pre {
    @apply absolute right-[40px] top-1/2 hidden translate-x-5 transform p-5 lg:block;
}

/* transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
